import { Icon, tokens } from '@hotjar/hotsauce';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';

const ScreenshotContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  background-color: ${tokens.color.neutral_800};
  color: ${tokens.color.white};
  border: 1px solid ${tokens.color.ui_border_light};
`;

const ScreenshotZoomPan = styled.div<{ increaseHeight: boolean }>`
  cursor: all-scroll;
  width: 100%;

  // Required to prevent strange Safari behavior with display: flex; and height: fit-content;
  // TODO: Investigate the issue further for workarounds
  max-height: ${(props) => (props.increaseHeight ? 600 : 500)}px;

  .react-transform-component {
    width: 100%;
    height: 100%;
  }

  .react-transform-element {
    width: 100%;
    height: 100%;
  }

  img {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    object-fit: contain;
  }
`;

const ScreenshotMissing = styled.div`
  opacity: 0.35;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  align-self: center;
  color: ${tokens.color.white};
`;

const ScreenshotLoading = styled(ScreenshotMissing)`
  opacity: 1;
  background-color: ${tokens.color.neutral_800};
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;

const ScreenshotText = styled.div`
  padding: ${tokens.spacer.x2} 0 0;
  ${tokens.text('body2')};
`;

interface ScreenshotProps {
  src: string | null;
  increaseHeight: boolean;
}

export function Screenshot({ src, increaseHeight }: ScreenshotProps): JSX.Element {
  const [imageState, setImageState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const { t } = useTranslation('feedback', { keyPrefix: 'feedback-response-modal.screenshot' });

  // show loader also when switching responses
  useEffect(() => {
    setImageState('loading');
  }, [src]);

  const resetTransformRef = useRef<() => void>();
  useEffect(() => {
    if (imageState === 'loaded') {
      resetTransformRef.current?.();
    }
  }, [imageState]);

  return (
    <ScreenshotContainer>
      {src === null || imageState === 'failed' ? (
        <ScreenshotMissing>
          <Icon size="xl" icon="image" />
          <ScreenshotText>{t('not-captured')}</ScreenshotText>
        </ScreenshotMissing>
      ) : (
        <ScreenshotZoomPan increaseHeight={increaseHeight}>
          {imageState === 'loading' && (
            <ScreenshotLoading>
              <div className="spinner spinner-xl" />
              <ScreenshotText>{t('loading')}</ScreenshotText>
            </ScreenshotLoading>
          )}
          <TransformWrapper>
            {(controls: { resetTransform: () => void }) => {
              resetTransformRef.current = controls.resetTransform;

              return (
                <TransformComponent>
                  <img
                    src={src}
                    alt={t('screenshot-alt')}
                    onLoad={() => setImageState('loaded')}
                    onError={() => setImageState('failed')}
                  />
                </TransformComponent>
              );
            }}
          </TransformWrapper>
        </ScreenshotZoomPan>
      )}
    </ScreenshotContainer>
  );
}
